/**
 * Step 1+2 (CRO audit, 2026-07): phone-model filter buttons inside the
 * unified "Visi filtri" drawer (snippets/collection-sidebar.liquid) used to
 * be plain <a href> facet links, causing a full page reload + lost scroll
 * position on every click — the top Clarity friction point on
 * smartphone-cases (23% quick-backs). This intercepts those clicks and
 * fetches/swaps the collection content itself, then syncs the URL via
 * history.pushState so back/forward restores the previous filter state.
 *
 * Originally this called the existing global window.ajaxfilter() (the same
 * swap color/price checkboxes use, assets/theme.js theme.collectionView) to
 * avoid duplicating that logic. Reverted after a live production bug
 * (2026-07-13): the live theme loads assets/theme.min.js, not theme.js, and
 * the two have drifted apart — theme.min.js's ajaxfilter() (a) never
 * returns the $.ajax() promise, so nothing chained off its call ever fires,
 * and (b) already calls history.pushState() itself at the end of its own
 * complete handler, contradicting theme.js's explicit "Do not pushState to
 * preserve cp param behaviour" comment. That undocumented, late pushState
 * races assets/aioo-model-normalize.js's enrichMbfRoot(), which reads
 * window.location.search to decide which model button is "active" — timing
 * depended on exactly when it ran relative to that hidden pushState, so it
 * was flaky. Worse, ajaxfilter()'s swap never refreshes
 * #aioo-picker-cta/#aioo-picker-count (collection-picker-cta.liquid), which
 * enrichMbfRoot()'s getActiveResultCount() reads to show the *real* count
 * for the active model — when active detection did accidentally line up,
 * that stale (frozen at initial unfiltered page load) count got written
 * into the button (customers saw e.g. "iPhone 17 Pro Max (1388)" instead of
 * the real ~35). Doing the swap here directly avoids both problems and lets
 * this code also keep the picker CTA's count in sync.
 *
 * Also handles [data-aioo-open-collection-filters] (collection-picker-cta.liquid),
 * which opens this same drawer instead of the site-wide device-selector
 * modal — the model picker now has a single UI on collection pages.
 */
(function () {
  'use strict';

  function isPlainClick(e) {
    return e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;
  }

  function hideMbfOverlays() {
    document.querySelectorAll('.mbf-loading-overlay.is-active').forEach(function (el) {
      el.classList.remove('is-active');
    });
  }

  /* Matches assets/theme.js ajaxfilter()'s swap list, plus #aioo-picker-cta
     (not touched by ajaxfilter(), which is what let it go stale). Uses
     jQuery's .html() when available so embedded <script> tags in the fresh
     markup (the mbf-root's own init script) re-execute, same as
     ajaxfilter() already relies on. */
  function applyCollectionResponse(doc) {
    var $ = window.jQuery;

    function swapHtml(selector) {
      var source = doc.querySelector(selector);
      if (!source) return;
      if ($) {
        $(selector).html(source.innerHTML);
      } else {
        var target = document.querySelector(selector);
        if (target) target.innerHTML = source.innerHTML;
      }
    }

    /* Update #aioo-picker-cta / #aioo-picker-count BEFORE .sidebar_tags: the
       mbf-root markup inside .sidebar_tags carries its own <script> that runs
       synchronously as soon as it's inserted (assets/aioo-model-normalize.js
       enrichMbfRoot()), and for the active model button it reads
       #aioo-picker-count's text via getActiveResultCount() to show the real
       count. Syncing the chip after the sidebar swap is one render too late —
       enrichMbfRoot() would already have read the stale value. */
    var freshCta = doc.getElementById('aioo-picker-cta');
    var liveCta = document.getElementById('aioo-picker-cta');
    if (freshCta && liveCta) {
      liveCta.setAttribute('data-product-count', freshCta.getAttribute('data-product-count') || '0');
    }
    var freshChipCount = doc.getElementById('aioo-picker-count');
    var liveChipCount = document.getElementById('aioo-picker-count');
    if (freshChipCount && liveChipCount) {
      liveChipCount.textContent = freshChipCount.textContent;
      liveChipCount.hidden = freshChipCount.hidden;
    }

    swapHtml('.productList .grid-products');
    swapHtml('.filters-toolbar__product-count');
    swapHtml('.sidebar_tags');
    swapHtml('.active-facets');

    var freshPagOuter = doc.querySelector('.infinitpaginOuter');
    if (freshPagOuter && freshPagOuter.innerHTML.trim()) {
      swapHtml('.infinitpaginOuter');
    } else {
      var pagin = document.querySelector('.infinitpagin');
      if (pagin) pagin.remove();
    }

    if (window.theme && window.theme.mlcurrency && typeof window.currenciesChange === 'function') {
      window.currenciesChange('sapn.money');
    }
    if (document.querySelector('.prRange') && typeof window.priceSlider === 'function') {
      window.priceSlider();
    }
    if (document.getElementById('sideProdSlider') && $ && $.fn && $.fn.flickity) {
      $('#sideProdSlider').flickity();
    }
    if (document.querySelector('.infinitpagin a.infinite') && typeof window.infiniteScroll === 'function') {
      window.infiniteScroll();
    }
    if (typeof window.loadMoreBtn === 'function') {
      window.loadMoreBtn();
    }
    /* "Rādīt vēl" sits outside .grid-products. Leaving the original href
       made page 2 of a model filter load the unfiltered collection. */
    if (typeof window.AIOO_syncLoadMoreFromDoc === 'function') {
      window.AIOO_syncLoadMoreFromDoc(doc);
    }
    /* Tell assets/aioo-ux.js's collection-picker-cta session state (model
       name text + localStorage.AIOO_selectedDevice) to re-read the URL we
       just pushState'd to — it only auto-syncs on the initial page load
       otherwise, see the listener there for the full incident. */
    document.dispatchEvent(new CustomEvent('aioo:collection-filters-updated'));
    document.querySelectorAll('.mbf-root[id]').forEach(function (el) {
      el.removeAttribute('data-mbf-init');
      if (window.AIOO_initMbf) window.AIOO_initMbf(el.id);
    });
  }

  /* Exposed so assets/aioo-ux.js can preview a filtered collection URL (does
     this model actually have products here?) before committing to it, and
     reuse the same grid/sidebar swap this file already does for clicks. */
  window.AIOO_applyCollectionResponseToDom = function (doc) {
    if (window.AIOO_facets && typeof window.AIOO_facets.applyDoc === 'function') return window.AIOO_facets.applyDoc(doc);
    return applyCollectionResponse(doc);
  };

  function fetchCollectionDoc(url) {
    return fetch(url, { credentials: 'same-origin' }).then(function (r) {
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.text();
    }).then(function (html) {
      return new DOMParser().parseFromString(html, 'text/html');
    });
  }
  window.AIOO_fetchCollectionDoc = fetchCollectionDoc;

  function setGridSoftLoading(on) {
    document.querySelectorAll('.grid-products').forEach(function (grid) {
      grid.classList.toggle('aioo-facets-grid--loading', !!on);
      grid.setAttribute('aria-busy', on ? 'true' : 'false');
    });
  }

  function runAjaxFilter(url, pushState) {
    /* Shared engine (assets/aioo-facets-ux.js): section rendering, abort, skeleton,
       sidebar state restore. The local path below is the fallback if it failed to load. */
    if (window.AIOO_facets && typeof window.AIOO_facets.load === 'function') {
      window.AIOO_facets.load(url, { push: !!pushState, immediate: true }).then(hideMbfOverlays, hideMbfOverlays);
      return;
    }
    if (pushState && window.history && window.history.pushState) {
      window.history.pushState({ aiooMbfFilter: true }, '', url);
    }
    setGridSoftLoading(true);
    var settled = false;
    function finish() {
      if (settled) return;
      settled = true;
      setGridSoftLoading(false);
      hideMbfOverlays();
    }
    var watchdog = setTimeout(finish, 12000);
    fetch(url, { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      })
      .then(function (html) {
        try {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          applyCollectionResponse(doc);
          if (typeof window.AIOO_trackUxEvent === 'function') {
            window.AIOO_trackUxEvent('filter_applied', { filter_type: 'model', page_path: window.location.pathname });
          }
          var gitems = document.querySelectorAll('.productList .grid-products .gitem');
          if (!gitems.length && typeof window.AIOO_trackUxEvent === 'function') {
            window.AIOO_trackUxEvent('filter_empty_result', { page_path: window.location.pathname });
          }
        } finally {
          clearTimeout(watchdog);
          finish();
        }
      })
      .catch(function () {
        clearTimeout(watchdog);
        finish();
        window.location.href = url;
      });
  }

  document.addEventListener('click', function (e) {
    var link = e.target.closest('.sidebar_tags a.mbf-btn, .sidebar_tags a.mbf-clear-all');
    if (link) {
      if (!isPlainClick(e)) return;
      var href = link.getAttribute('href');
      if (!href) return;
      try {
        var nextUrl = new URL(href, window.location.origin);
        var curPath = (window.location.pathname || '/').replace(/\/+$/, '') || '/';
        var nextPath = (nextUrl.pathname || '/').replace(/\/+$/, '') || '/';
        var isCollectionJump = nextPath !== curPath
          || link.getAttribute('data-mbf-collection-link') === '1';

        if (link.classList.contains('mbf-clear-all')) {
          try { localStorage.removeItem('AIOO_selectedDevice'); } catch (_ls) { /* ignore */ }
          nextUrl.searchParams.delete('aioo_model');
        } else if (link.classList.contains('active') || link.classList.contains('active-compat')) {
          try { localStorage.removeItem('AIOO_selectedDevice'); } catch (_ls2) { /* ignore */ }
          nextUrl.searchParams.delete('aioo_model');
        } else {
          var pickedModel = link.dataset.model || '';
          if (pickedModel) {
            try { localStorage.setItem('AIOO_selectedDevice', pickedModel); } catch (_ls3) { /* ignore */ }
            if (typeof window.AIOO_setUserModelParam === 'function') {
              window.AIOO_setUserModelParam(nextUrl, pickedModel);
            } else {
              nextUrl.searchParams.set('aioo_model', pickedModel);
            }
          }
        }

        /* Hub → model/brand subcollection: full navigation so S&D facets load.
           Keep brand, colour and price already chosen here. The destination
           link replaces the model; the page index must not come along. */
        if (isCollectionJump && !link.classList.contains('mbf-clear-all')) {
          e.preventDefault();
          try {
            var liveJump = new URL(window.location.href);
            liveJump.searchParams.forEach(function (val, key) {
              if (key === 'page' || key === 'aioo_model') return;
              if (key.indexOf('compatible_model') !== -1) return;
              if (key.indexOf('filter.') !== 0 && key !== 'sort_by') return;
              var already = false;
              nextUrl.searchParams.forEach(function (_v, k) {
                if (k === key) already = true;
              });
              if (!already) nextUrl.searchParams.append(key, val);
            });
          } catch (_keep) { /* destination href stands */ }
          hideMbfOverlays();
          if (window.AIOO_clearCollectionChrome) window.AIOO_clearCollectionChrome();
          window.location.assign(nextUrl.pathname + nextUrl.search + (nextUrl.hash || ''));
          return;
        }

        e.preventDefault();
        href = nextUrl.pathname + nextUrl.search + (nextUrl.hash || '');
      } catch (_urlErr) {
        e.preventDefault();
      }
      runAjaxFilter(href, true);
      return;
    }

    var openFiltersBtn = e.target.closest('[data-aioo-open-collection-filters]');
    if (openFiltersBtn) {
      e.preventDefault();
      document.querySelectorAll('.filterbar').forEach(function (el) {
        el.classList.add('active');
      });
    }
  });

  window.addEventListener('popstate', function () {
    if (window.AIOO_facets) return;
    /* Back from a full navigation restores the document (often from bfcache)
       with the "Filtrē..." overlay still up. Drop that immediately. Refetch
       only for same-document filter history — a bfcache restore is already
       the right page, and parsing it again freezes the tab. */
    if (window.AIOO_clearCollectionChrome) window.AIOO_clearCollectionChrome({ closeDrawer: true });
    else hideMbfOverlays();
    setTimeout(function () {
      if (window.AIOO_bfcacheHold) return;
      if (!document.querySelector('.sidebar_tags .mbf-root')) return;
      runAjaxFilter(window.location.href, false);
    }, 60);
  });
})();
